// src/pages/Tutorial.jsx
import React, { useRef, useEffect } from 'react';
import { motion, useScroll } from 'framer-motion';
import { ChevronDown, Sparkles, ArrowRight, Check, Wallet, ShoppingCart, History, Package } from 'lucide-react';

const sections = [
  {
    id: 1,
    title: "ثبت نام سریع",
    description: "در کمتر از ۱ دقیقه حساب کاربری بسازید. فقط شماره موبایل و کد ملی و تاریخ تولد کافی است.",
    image: "/images/tutorial/register.png",
    icon: Sparkles,
    bg: "from-amber-50 to-yellow-50"
  },
  {
    id: 2,
    title: "شارژ کیف پول",
    description: "ابتدا کیف پول خود را با شارژ کنید. و فیش آن را ارسال کنید.",
    image: "/images/tutorial/deposit.png",
    icon: Wallet,
    bg: "from-emerald-50 to-teal-50"
  },
  {
    id: 3,
    title: "خرید یا فروش طلا",
    description: "با قیمت لحظه‌ای طلا بخرید یا بفروشید..",
    image: "/images/tutorial/trade.png",
    icon: ShoppingCart,
    bg: "from-indigo-50 to-purple-50"
  },
  {
    id: 4,
    title: "تاریخچه تراکنش‌ها",
    description: "همه خریدها، فروش‌ها و برداشت‌ها با جزئیات کامل در دسترس شماست.",
    image: "/images/tutorial/history.png",
    icon: History,
    bg: "from-pink-50 to-rose-50"
  },
  {
    id: 5,
    title: "درخواست آبشده",
    description: "طلای خود را انتخاب وبه صورت فیزیکی بدون پرداخت هیچ کارمزدی دریافت کنید. تحویل در شعب.",
    image: "/images/tutorial/gold-request.png",
    icon: Package,
    bg: "from-cyan-50 to-sky-50",
    cta: true
  }
];

export default function Tutorial() {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  // پیش‌لود عکس اول
  useEffect(() => {
    const img = new Image();
    img.src = sections[0].image;
  }, []);

  return (
    <div ref={containerRef} className="bg-gradient-to-b from-slate-50 to-white overflow-x-hidden">
      {/* هدر ثابت */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <h1 className="text-2xl font-bold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
            آموزش گام به گام
          </h1>
          <button onClick={() => window.location.href = '/'} className="text-sm text-amber-600 hover:text-amber-700 font-medium">
            رد کردن
          </button>
        </div>
      </div>

      {/* نوار پیشرفت */}
      <motion.div className="fixed top-16 left-0 right-0 h-1 bg-gray-200 z-40" style={{ scaleX: scrollYProgress }}>
        <div className="h-full bg-gradient-to-r from-amber-500 to-orange-500" />
      </motion.div>

      {/* فاصله از بالا */}
      <div className="pt-20" />

      {/* هر بخش با min-h-screen برای موبایل + padding کافی */}
      {sections.map((section, index) => {
        const Icon = section.icon;
        const isFirst = index === 0;

        return (
          <section
            key={section.id}
            className="relative min-h-screen flex items-center justify-center px-6 py-16 md:py-24"
          >
            {/* پس‌زمینه گرادیان */}
            <div className={`absolute inset-0 bg-gradient-to-br ${section.bg} opacity-30`} />
            
            <div className="relative max-w-5xl mx-auto w-full grid md:grid-cols-2 gap-12 items-center">
              {/* تصویر */}
              <motion.div
                {...(isFirst
                  ? { initial: { x: -100, opacity: 0 }, animate: { x: 0, opacity: 1 } }
                  : { initial: { x: -100, opacity: 0 }, whileInView: { x: 0, opacity: 1 } }
                )}
                viewport={isFirst ? {} : { once: true, margin: "-150px" }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="order-2 md:order-1"
              >
                <div className="relative">
                  <div className="absolute -inset-4 bg-gradient-to-r from-amber-400 to-orange-500 rounded-3xl blur-xl opacity-30" />
                  <img
                    src={section.image}
                    alt={section.title}
                    loading={isFirst ? "eager" : "lazy"}
                    className="relative rounded-3xl shadow-2xl w-full max-w-md mx-auto border border-gray-200"
                  />
                </div>
              </motion.div>

              {/* متن */}
              <motion.div
                {...(isFirst
                  ? { initial: { x: 100, opacity: 0 }, animate: { x: 0, opacity: 1 } }
                  : { initial: { x: 100, opacity: 0 }, whileInView: { x: 0, opacity: 1 } }
                )}
                viewport={isFirst ? {} : { once: true, margin: "-150px" }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="order-1 md:order-2 text-right"
              >
                <div className="flex items-center justify-end gap-2 mb-4">
                  <Icon className="w-6 h-6 text-amber-600" />
                  <span className="text-sm font-bold text-amber-600">مرحله {section.id}</span>
                </div>
                
                <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6 leading-tight">
                  {section.title}
                </h2>
                
                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                  {section.description}
                </p>

                {section.cta ? (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => window.location.href = '/register_page'}
                    className="bg-gradient-to-r from-amber-500 to-orange-600 text-white px-8 py-4 rounded-2xl font-bold text-lg shadow-xl hover:shadow-2xl flex items-center gap-3"
                  >
                  همین حالا شروع کن
                    <ArrowRight className="w-6 h-6" />
                  </motion.button>
                ) : (
                  <div className="flex items-center justify-end gap-2 text-green-600">
                    <Check className="w-5 h-5" />
                    <span className="text-sm font-medium">اسکرول کنید تا ادامه دهید</span>
                  </div>
                )}
              </motion.div>
            </div>

            {/* نشانگر اسکرول */}
            {index < sections.length - 1 && (
              <motion.div
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
                className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
              >
                <ChevronDown className="w-8 h-8 text-gray-400" />
              </motion.div>
            )}
          </section>
        );
      })}

      {/* دکمه شناور */}
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })}
        className="fixed bottom-8 left-8 bg-gradient-to-r from-amber-500 to-orange-600 text-white p-4 rounded-full shadow-2xl z-50"
      >
        <ChevronDown className="w-6 h-6" />
      </motion.button>
    </div>
  );
}